import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

// Types
export type SpanEvent = {
  name: string;
  time: string;
  attributes: Record<string, unknown>;
};

export type TraceSpan = {
  id: number | string;
  spanId: string;
  traceId: string;
  parentId: string | null;
  name: string;
  spanKind: string;
  startTime: string;
  endTime: string;
  durationMs: number;
  statusCode: string;
  statusMessage: string | null;
  attributes: Record<string, unknown>;
  events?: SpanEvent[];
  organizationId: string | null;
  agentId: string | null;
  sessionId: string | null;
  eventName: string | null;
  turnNumber: number | null;
};

export type SpanPreview = {
  spanId: string;
  traceId: string;
  parentId: string | null;
  name: string;
  spanKind: string;
  eventName: string | null;
  turnNumber: number | null;
  startTime: string;
  endTime: string;
  durationMs: number;
  statusCode: string;
  statusMessage: string | null;
  preview: string | null;
  voicerunSpanType?: string;
  forceClosed?: boolean;
  interrupted?: boolean;
  openinferenceSpanKind?: string;
  completionProvider?: string;
  completionModel?: string;
  vrKeyUsed?: boolean;
  costUsd?: number;
  costUnmappableReason?: string;
};

export type TraceSummary = {
  id: number;
  publicId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  traceId: string;
  sessionId: string;
  startTime: string;
  endTime: string;
  durationMs: number;
  spanCount: number;
  rootSpanName: string | null;
  hasError: boolean;
  totalCostUsd: number | null;
};

export type SessionTraceData = {
  trace: TraceSummary | null;
  spans: TraceSpan[];
};

export type SessionTraceOverviewData = {
  trace: TraceSummary;
  spans: SpanPreview[];
};

export type SessionTraceParams = {
  agentId: string;
  sessionId: string;
  page?: number;
  limit?: number;
};

export type SpanDetailParams = {
  agentId: string;
  sessionId: string;
  spanId: string;
};

export type SessionTraceMetadata = {
  total: number;
  page: number;
  limit: number;
};

export type SessionTraceState = {
  currentTrace: SessionTraceData | null;
  currentSessionId: string | null;
  metadata: SessionTraceMetadata | null;
  isLoading: boolean;
  error: string | null;
  overviewSpans: SpanPreview[] | null;
  overviewPage: number;
  overviewTotal: number;
  overviewHasMore: boolean;
  isLoadingMore: boolean;
  spanDetailCache: Record<string, TraceSpan>;
  loadingSpanId: string | null;
};

// Async thunk for fetching session trace
export const getSessionTrace = createAsyncThunk("sessionTrace/getTrace", async (params: SessionTraceParams) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set("page", String(params.page));
  if (params.limit) queryParams.set("limit", String(params.limit));
  const qs = queryParams.toString();
  const format = qs ? `format=json&${qs}` : "format=json";
  const url = `/v1/sessions/${params.sessionId}/trace-full?${format}`;
  const { data, metadata } = await makeRequest<{ data: SessionTraceData; metadata?: SessionTraceMetadata }>(url);
  return { data, metadata: metadata || null, sessionId: params.sessionId };
});

export const getSessionTraceOverview = createAsyncThunk("sessionTrace/getOverview", async (params: { agentId: string; sessionId: string }) => {
  const url = `/v1/sessions/${params.sessionId}/trace?page=1&limit=500`;
  const { data, metadata } = await makeRequest<{ data: SessionTraceOverviewData | null; metadata?: SessionTraceMetadata }>(url);
  return { data, metadata: metadata || null, sessionId: params.sessionId };
});

export const getSessionTraceOverviewPage = createAsyncThunk(
  "sessionTrace/getOverviewPage",
  async (params: { agentId: string; sessionId: string; page: number }) => {
    const url = `/v1/sessions/${params.sessionId}/trace?page=${params.page}&limit=500`;
    const { data, metadata } = await makeRequest<{ data: SessionTraceOverviewData | null; metadata?: SessionTraceMetadata }>(url);
    return { data, metadata: metadata || null, sessionId: params.sessionId };
  },
);

export const getSpanDetail = createAsyncThunk("sessionTrace/getSpanDetail", async (params: SpanDetailParams) => {
  const url = `/v1/sessions/${params.sessionId}/span/${params.spanId}`;
  const { data } = await makeRequest<{ data: TraceSpan }>(url);
  return { span: data, spanId: params.spanId };
});

const initialState: SessionTraceState = {
  currentTrace: null,
  currentSessionId: null,
  metadata: null,
  isLoading: false,
  error: null,
  overviewSpans: null,
  overviewPage: 1,
  overviewTotal: 0,
  overviewHasMore: false,
  isLoadingMore: false,
  spanDetailCache: {},
  loadingSpanId: null,
};

const sessionTraceSlice = createSlice({
  name: "sessionTrace",
  initialState,
  reducers: {
    clearTrace: (state) => {
      state.currentTrace = null;
      state.currentSessionId = null;
      state.metadata = null;
      state.error = null;
      state.overviewSpans = null;
      state.overviewPage = 1;
      state.overviewTotal = 0;
      state.overviewHasMore = false;
      state.isLoadingMore = false;
      state.spanDetailCache = {};
      state.loadingSpanId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSessionTrace.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getSessionTrace.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentTrace = action.payload.data;
      state.currentSessionId = action.payload.sessionId;
      state.metadata = action.payload.metadata;
      state.error = null;
    });
    builder.addCase(getSessionTrace.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch session trace";
    });
    builder.addCase(getSessionTraceOverview.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getSessionTraceOverview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.currentSessionId = action.payload.sessionId;
      if (action.payload.data) {
        state.currentTrace = { trace: action.payload.data.trace, spans: [] };
        state.overviewSpans = action.payload.data.spans;
        if (action.payload.metadata) {
          const meta = action.payload.metadata;
          state.overviewPage = meta.page;
          state.overviewTotal = meta.total;
          state.overviewHasMore = meta.page * meta.limit < meta.total;
        } else {
          state.overviewHasMore = false;
        }
      } else {
        state.currentTrace = null;
        state.overviewSpans = null;
        state.overviewHasMore = false;
      }
    });
    builder.addCase(getSessionTraceOverview.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch trace overview";
    });
    builder.addCase(getSessionTraceOverviewPage.pending, (state) => {
      state.isLoadingMore = true;
    });
    builder.addCase(getSessionTraceOverviewPage.fulfilled, (state, action) => {
      state.isLoadingMore = false;
      if (action.payload.data) {
        const newSpans = action.payload.data.spans;
        if (state.overviewSpans) {
          const existingIds = new Set(state.overviewSpans.map((s) => s.spanId));
          const deduped = newSpans.filter((s) => !existingIds.has(s.spanId));
          state.overviewSpans = [...state.overviewSpans, ...deduped];
        } else {
          state.overviewSpans = newSpans;
        }
        if (action.payload.metadata) {
          const meta = action.payload.metadata;
          state.overviewPage = meta.page;
          state.overviewTotal = meta.total;
          state.overviewHasMore = meta.page * meta.limit < meta.total;
        }
      }
    });
    builder.addCase(getSessionTraceOverviewPage.rejected, (state) => {
      state.isLoadingMore = false;
    });
    builder.addCase(getSpanDetail.pending, (state, action) => {
      state.loadingSpanId = action.meta.arg.spanId;
    });
    builder.addCase(getSpanDetail.fulfilled, (state, action) => {
      state.loadingSpanId = null;
      state.spanDetailCache[action.payload.spanId] = action.payload.span;
    });
    builder.addCase(getSpanDetail.rejected, (state) => {
      state.loadingSpanId = null;
    });
  },
});

export const { clearTrace } = sessionTraceSlice.actions;
export const sessionTraceReducer = sessionTraceSlice.reducer;
